// src/components/ui/AppTile.tsx — tile button used by the Apps grid and the
// Home quick-links section.
//
// Port of docs/prototype/shell.jsx 47-75 with the audit-resolved overrides:
//   - Brass top-edge gradient is uniform across all tiles (matches the Card
//     primitive's edge prop; the prototype's per-tone stripe was dropped to
//     reduce visual noise).
//   - The icon disc remains per-tone so the tone palette still reads.
//
// iconKey is a string lookup into the Icon module so non-React callers
// (apps registry, server-side rendering) can store the key without holding
// a React component reference.

import type { CSSProperties, ReactElement } from "react";
import { C, F } from "../../theme";
import * as Icons from "./Icon";
import type { IconProps } from "./Icon";

type IconComponent = (props: IconProps) => ReactElement;

function iconFor(key: string): IconComponent | null {
  const entry = (Icons as unknown as Record<string, unknown>)[key];
  return typeof entry === "function" ? (entry as IconComponent) : null;
}

export type AppTileTone = "brass" | "heather" | "sage" | "signal" | "paper";
export type AppTileSize = "lg" | "sm";

export interface AppTileProps {
  name: string;
  desc: string;
  iconKey: string;
  tone?: AppTileTone;
  size?: AppTileSize;
  onTap: () => void;
}

interface ToneStyle {
  bg: string;
  ic: string;
}

const TONES: Record<AppTileTone, ToneStyle> = {
  brass:   { bg: C.brassBg,   ic: C.brassDk   },
  heather: { bg: C.heatherBg, ic: C.heatherDk },
  sage:    { bg: C.sageBg,    ic: C.sageDk    },
  signal:  { bg: C.signalBg,  ic: C.signalDk  },
  paper:   { bg: C.paperWarm, ic: C.brassDk   },
};

export const AppTile = ({
  name,
  desc,
  iconKey,
  tone = "brass",
  size = "lg",
  onTap,
}: AppTileProps) => {
  const t = TONES[tone];
  const Icon = iconFor(iconKey);
  const compact = size === "sm";

  const buttonStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    background: C.card,
    border: `1px solid ${C.hairlineLt}`,
    borderRadius: 18,
    padding: compact ? "12px 12px" : "16px 14px",
    minHeight: compact ? 92 : 128,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: C.shadowS,
    fontFamily: F.body,
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
    position: "relative",
    overflow: "hidden",
    width: "100%",
  };

  return (
    <button type="button" onClick={onTap} style={buttonStyle}>
      {/* Uniform brass top-edge stripe (audit override) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 18,
          right: 18,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${C.brassLt} 20%, ${C.brassLt} 80%, transparent)`,
          opacity: 0.5,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />

      <div
        style={{
          width: compact ? 40 : 48,
          height: compact ? 40 : 48,
          borderRadius: 13,
          background: t.bg,
          border: `1px solid ${C.hairlineLt}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: compact ? 8 : 12,
        }}
      >
        {Icon && <Icon s={compact ? 20 : 24} c={t.ic} />}
      </div>

      <div
        style={{
          fontFamily: F.serif,
          fontSize: compact ? 15 : 17,
          fontWeight: 700,
          color: C.ink,
          lineHeight: 1.15,
          letterSpacing: -0.1,
        }}
      >
        {name}
      </div>
      <div
        style={{
          fontSize: compact ? 12 : 13,
          color: C.inkSoft,
          marginTop: 3,
          fontWeight: 600,
          lineHeight: 1.3,
        }}
      >
        {desc}
      </div>
    </button>
  );
};
